<template>
  <div>
    <div class="flex justify-center items-center h-screen" v-show="loading">
      <UISpin class="text-primary" />
    </div>

    <div ref="builder" id="email-builder-container" v-show="!loading"></div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, onMounted, onUnmounted, ref } from 'vue'
import Postmate from 'postmate'
import { useStore } from '../store'
import { ActionTypes } from '../store/actions'
import { useRoute, onBeforeRouteLeave } from 'vue-router'
import { UISpin } from '@gohighlevel/ghl-ui'
import config from '../config'
import { useBuilder, builderState } from 'common/composable/manageBuilderState'
import i18n from 'common/locales'
import { appState } from 'common/composable/appState'

export default defineComponent({
  components: { UISpin },
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const loading = ref(false)
    const builder = ref()

    function updateToken(token: string) {
      store.dispatch(ActionTypes.UPDATE_TOKEN, token)
    }

    onBeforeRouteLeave(function (to, from, next) {
      if (builderState.unsavedChanges) {
        if (
          window.confirm(
            'It looks like you have been editing something.If you leave before saving, your changes will be lost.'
          )
        )
          next()
        else next(false)
      }
      next()
    })

    const { updateUnsavedChanges, updateUndoRedoEvents } = useBuilder(store)
    const handshake = ref()
    onMounted(() => {
      loading.value = true
      const route = useRoute()
      if (process.env.NODE_ENV === 'development') {
        Postmate.debug = true
      }

      const modelParams = {
        locationId: store.state.locationId,
        companyId: store.state.companyId,
        templateId: store.state.emailId,
        userId: store.state.userId,
        apiKey: store.state.apiKey,
        tokenId: store.state.tokenId,
        email: store.state.userEmail,
        enableContentAI: store.state.userPermissions?.enableContentAI,
        customValues: [],
        locale: i18n.global.locale.value,
      }
      if (builderState.customValues)
        modelParams.customValues = JSON.parse(
          JSON.stringify(builderState.customValues)
        )

      const url =
        builderState.isNewBuilder && config.newBuilderURL
          ? config.newBuilderURL
          : config.builderAppUrl
      handshake.value = new Postmate({
        container: builder.value,
        url,
        name: 'email-builder',
        model: modelParams,
      })

      handshake.value.then((child: any) => {
        const builderIframeElement = document.querySelector('#email-builder-container iframe')
        if(appState.isModalDialog && builderIframeElement) builderIframeElement.style.height = 'calc(100vh - 96px)'

        loading.value = false
        store.dispatch(ActionTypes.ADD_BUILDER, handshake)
        child.on('init-builder', (data: any) => {
          builderState.previewUrl = data.previewUrl
          builderState.updatedAt = data.updatedAt
        })
        child.on('rss-element-status', (data: any) => {
          const { hasRSSElement } = data
          builderState.hasRSSElement = hasRSSElement
        })
        child.on('unsaved-changes', (data: any) => {
          //@ts-ignore
          updateUnsavedChanges()
        })
        child.on('undo-redo-events', (data: any) => {
          //@ts-ignore
          const { historyQueueLength, historyPosition } = data
          updateUndoRedoEvents({ historyQueueLength, historyPosition })
        })
        child.on('refresh-token', async (data: any) => {
          if (window.getToken) {
            // @ts-expe ct-error
            const token = await window.getToken()
            child.call('updated-token', token)
            updateToken(token)
          }
        })
        child.on('media-libary-open', () => (builderState.mediaOpen = true))
        child.on('media-library-close', () => (builderState.mediaOpen = false))
        child.on('builder-response-error', () => {
          builderState.errorState = true
        })

        child.on('content-ai-variations-generate', (payload: any) => {
          emitter.emit('email_preview-content-ai-variations-generate', payload)
        })
        child.on('content-ai-quick-action', (payload: any) => {
          emitter.emit('email_preview-content-ai-quick-action', payload)
        })
        child.on('image-ai-generate', () => {
          emitter.emit('email_preview-image-ai-generate')
        })
        child.on('content-ai-variations-show', (id: string) => {
          emitter.emit('email_preview-content-ai-variations-show', id)
        })
        child.on('image-ai-variations-show', (id: string) => {
          emitter.emit('email_preview-image-ai-variations-show', id)
        })
      })
    })
    onUnmounted(() => {
      handshake.value.then((child: any) => {
        child.destroy()
      })
      store.dispatch(ActionTypes.ADD_BUILDER, '')
    })

    return {
      builder,
      loading,
    }
  },
})
</script>

<style>
#email-builder-container iframe {
  width: 100%;
  height: calc(100vh - 56px);
}
</style>
